/** @jsxImportSource theme-ui */
import React, { useEffect, useRef } from 'react';
import { Box, Flex } from 'theme-ui';
import { formatLogEntry, PreviewLogEntry } from './previewLog';

const VISIBLE_LINES = 8;
const LINE_HEIGHT = 1.5;

const HEADER_TEXT = 'Console · ← received from the widget · → sent to the widget';
const EMPTY_TEXT = '— no events or commands yet —';

interface Props {
    entries: PreviewLogEntry[];
    onClear: () => void;
}

const PreviewConsole: React.FC<Props> = ({ entries, onClear }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const node = scrollRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [entries]);

    return (
        <Box sx={{ mt: 'auto', pt: 3, pb: 2 }}>
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 2,
                    fontFamily: 'monospace',
                    fontSize: 0,
                    lineHeight: LINE_HEIGHT,
                    borderRadius: 'sm',
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                }}
            >
                <Flex sx={{ px: 2, pt: 2, color: '#808080', justifyContent: 'space-between' }}>
                    <Box>{HEADER_TEXT}</Box>
                    <button
                        type="button"
                        onClick={onClear}
                        disabled={entries.length === 0}
                        sx={{
                            fontFamily: 'inherit',
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                            color: '#d4d4d4',
                            bg: 'transparent',
                            border: '1px solid #555',
                            borderRadius: 'sm',
                            px: 2,
                            cursor: 'pointer',
                            '&:disabled': { color: '#666', cursor: 'default' },
                        }}
                    >
                        Clear
                    </button>
                </Flex>
                <Box
                    ref={scrollRef}
                    role="log"
                    aria-label="Preview console"
                    sx={{
                        height: `${VISIBLE_LINES * LINE_HEIGHT}em`,
                        overflowY: 'auto',
                        px: 2,
                        pb: 2,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                    }}
                >
                    {entries.length === 0 && <Box sx={{ color: '#808080' }}>{EMPTY_TEXT}</Box>}
                    {entries.map((entry, index) => (
                        <Box
                            key={`${entry.at.getTime()}-${index}`}
                            data-testid="console-line"
                            sx={{ color: entry.direction === 'out' ? '#9cdcfe' : '#d4d4d4' }}
                        >
                            {formatLogEntry(entry)}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default PreviewConsole;
